import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, getPost } from './api'

interface NewPostProps {
  mode?: 'create' | 'edit';
  slug?: string;
}

export default function NewPost({ mode = 'create', slug }: NewPostProps) {
  const navigate = useNavigate()
  const isEdit = mode === 'edit'
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: '정육',
    tags: '',
    excerpt: '',
    metaDescription: '',
    content: '',
    featured: false
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Load post data in edit mode
  useEffect(() => {
    const token = localStorage.getItem('admin_token')
    if (!token) {
      navigate('/login')
      return
    }

    if (mode === 'edit' && slug) {
      setLoading(true)
      getPost(slug)
        .then((post) => {
          if (post) {
            setFormData({
              title: post.title,
              slug: post.slug,
              content: post.content || '',
              excerpt: post.excerpt || '',
              category: post.category || '정육',
              tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
              metaDescription: post.metaDescription || '',
              featured: post.featured || false,
            })
          }
          setLoading(false)
        })
        .catch(() => {
          setError('포스트를 불러오는데 실패했습니다.')
          setLoading(false)
        })
    }
  }, [mode, slug, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const token = localStorage.getItem('admin_token')
    if (!token) {
      navigate('/login')
      return
    }

    try {
      const postData = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        date: new Date().toISOString()
      }

      if (isEdit) {
        await api.updatePost(token, slug!, postData)
      } else {
        await api.createPost(token, postData)
      }
      navigate('/')
    } catch (err) {
      setError(isEdit ? '게시글 수정 중 오류가 발생했습니다.' : '게시글 생성 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← 돌아가기
              </button>
            </div>
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">{isEdit ? '포스트 수정' : '새 게시글 작성'}</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    제목 *
                  </label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Slug
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                    placeholder="자동 생성됨"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    disabled={isEdit}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {isEdit ? '수정 모드에서는 변경할 수 없습니다' : '비워두면 자동 생성됩니다'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    카테고리 *
                  </label>
                  <select
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="정육">정육</option>
                    <option value="축산">축산</option>
                    <option value="요리">요리</option>
                    <option value="식육가공">식육가공</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    태그 (쉼표로 구분)
                  </label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="정육, 한우, 육류"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    요약
                  </label>
                  <textarea
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    메타 설명
                  </label>
                  <textarea
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={formData.metaDescription}
                    onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  />
                  <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                    추천 게시글
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    내용 *
                  </label>
                  <textarea
                    required
                    rows={15}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-mono"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="# 제목&#10;&#10;내용을 Markdown으로 작성하세요..."
                  />
                  <p className="mt-1 text-xs text-gray-500">Markdown 형식으로 작성하세요</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? '저장 중...' : (isEdit ? '수정' : '게시')}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}
